import {
    browserLocalPersistence,
    createUserWithEmailAndPassword,
    getAuth,
    getRedirectResult,
    GoogleAuthProvider,
    sendPasswordResetEmail,
    setPersistence,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    User as FirebaseUser
} from "firebase/auth";
import {createContext, PropsWithChildren, useEffect, useState} from "react";
import AuthButton from "./AuthButton";
import {firebaseApp, isLocalHost} from "../firebaseClient";

const auth = getAuth(firebaseApp);
const provider = new GoogleAuthProvider();
provider.setCustomParameters({
    prompt: "select_account"
});
const tokenLength = (token?: string) => token?.length || 0;
const E2E_TOKEN = "e2e-token";
const E2E_AUTH_EMAIL = "e2e@soundspots.local";
const E2E_AUTH_USER = ({email: E2E_AUTH_EMAIL, uid: "e2e-user"} as FirebaseUser);
const isE2EAuthBypassEnabled = () => {
    if (!isLocalHost || typeof window === "undefined") {
        return false;
    }
    const search = new URLSearchParams(window.location.search || "");
    return search.get("e2eAuth") === "1";
};
let persistenceReadyPromise: Promise<void> | null = null;

const ensurePersistenceReady = async () => {
    if (!persistenceReadyPromise) {
        persistenceReadyPromise = setPersistence(auth, browserLocalPersistence)
            .then(() => {
                console.log("[auth-trace] Firebase auth persistence set to local");
            });
    }
    return persistenceReadyPromise;
};


export interface AuthContextI {
    getAuthUser: () => FirebaseUser | null,
    getToken: () => string | undefined,
    getTokenAsync: () => Promise<string | undefined>,
    signIn: () => void,
    signInWithEmail: (email: string, password: string) => Promise<void>,
    signUpWithEmail: (email: string, password: string) => Promise<void>,
    sendPasswordReset: (email: string) => Promise<void>,
    signOut: () => void,
    refreshToken: () => Promise<string | undefined>
}

export const AuthContext = createContext<AuthContextI>({
    getAuthUser: () => null,
    getToken: () => undefined,
    getTokenAsync: async () => undefined,
    signIn: () => null,
    signInWithEmail: async () => undefined,
    signUpWithEmail: async () => undefined,
    sendPasswordReset: async () => undefined,
    signOut: () => null,
    refreshToken: async () => undefined
});

const FirebaseAuth = (props: PropsWithChildren<any>) => {
    const [authUser, setAuthUser] = useState<FirebaseUser | null>(null);
    const [token, setToken] = useState<string | undefined>(undefined);
    const [isAuthReady, setAuthReady] = useState<boolean>(false);
    const e2eBypassAuth = isE2EAuthBypassEnabled();


    useEffect(() => {
        if (e2eBypassAuth) {
            console.log("[auth-trace] e2eAuth bypass enabled");
            setAuthUser(E2E_AUTH_USER);
            setToken(E2E_TOKEN);
            setAuthReady(true);
            return;
        }

        let isMounted = true;
        const unsubscribe = auth.onIdTokenChanged((userResult) => {
            console.log("[auth-trace] onIdTokenChanged", {
                hasUser: !!userResult,
                email: userResult?.email || null,
                uid: userResult?.uid || null
            });
            if (!isMounted) {
                return;
            }
            setAuthUser(userResult);
            if (userResult) {
                userResult.getIdToken()
                    .then((idToken) => {
                        console.log("[auth-trace] onIdTokenChanged token resolved", {
                            email: userResult.email || null,
                            tokenLength: tokenLength(idToken)
                        });
                        setToken(idToken);
                    })
                    .catch((error) => {
                        console.error("Error retrieving auth token:", error);
                        setToken(undefined);
                    })
                    .finally(() => {
                        setAuthReady(true);
                    });
                return;
            }

            setToken(undefined);
            setAuthReady(true);
        });

        const initializeAuthFlow = async () => {
            console.log("[auth-trace] Initializing Firebase auth persistence");
            try {
                await ensurePersistenceReady();
            } catch (error) {
                console.error("Error setting persistence:", error);
            }

            if (!isLocalHost) {
                try {
                    const result = await getRedirectResult(auth);
                    console.log("[auth-trace] getRedirectResult resolved", {
                        hasUser: !!result?.user,
                        email: result?.user?.email || null,
                        providerId: result?.providerId || null
                    });
                } catch (error: any) {
                    console.error("[auth-trace] getRedirectResult failed", {
                        code: error?.code || null,
                        message: error?.message || null,
                        email: error?.customData?.email || null
                    }, error);
                }
            }
        };

        void initializeAuthFlow();

        // Clean up subscription on unmount
        return () => {
            isMounted = false;
            unsubscribe();
        };
    }, [e2eBypassAuth]);


    const signIn = async () => {
        if (e2eBypassAuth) {
            setAuthUser(E2E_AUTH_USER);
            setToken(E2E_TOKEN);
            setAuthReady(true);
            return;
        }
        try {
            await ensurePersistenceReady();
            if (isLocalHost) {
                console.log("[auth-trace] Starting Google sign-in popup (localhost)");
                await signInWithPopup(auth, provider);
            } else {
                console.log("[auth-trace] Starting Google sign-in redirect");
                await signInWithRedirect(auth, provider);
            }
        } catch (error) {
            console.error("Error signing in:", error);
        }
    };

    const signInWithEmail = async (email: string, password: string) => {
        if (e2eBypassAuth) {
            setAuthUser(E2E_AUTH_USER);
            setToken(E2E_TOKEN);
            setAuthReady(true);
            return;
        }
        await ensurePersistenceReady();
        console.log("[auth-trace] Starting email/password sign-in", {email});
        await signInWithEmailAndPassword(auth, email, password);
    };

    const signUpWithEmail = async (email: string, password: string) => {
        if (e2eBypassAuth) {
            setAuthUser(E2E_AUTH_USER);
            setToken(E2E_TOKEN);
            setAuthReady(true);
            return;
        }
        await ensurePersistenceReady();
        console.log("[auth-trace] Starting email/password sign-up", {email});
        await createUserWithEmailAndPassword(auth, email, password);
    };

    const sendPasswordReset = async (email: string) => {
        if (e2eBypassAuth) {
            return;
        }
        console.log("[auth-trace] Sending password reset", {email});
        await sendPasswordResetEmail(auth, email);
    };

    const signOut = async () => {
        if (e2eBypassAuth) {
            setToken(undefined);
            setAuthUser(null);
            return;
        }
        try {
            console.log("[auth-trace] Signing out");
            await auth.signOut();
            setAuthUser(null);
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const getTokenAsync = async () => {
        if (e2eBypassAuth) {
            setToken(E2E_TOKEN);
            return E2E_TOKEN;
        }
        const user = auth.currentUser;
        if (!user) {
            console.warn("[auth-trace] getTokenAsync: no current user");
            setToken(undefined);
            return undefined;
        }
        try {
            const idToken = await user.getIdToken();
            console.log("[auth-trace] getTokenAsync resolved token", {
                email: user.email || null,
                tokenLength: tokenLength(idToken)
            });
            setToken(idToken);
            return idToken;
        } catch (error) {
            console.error("Error retrieving auth token:", error);
            setToken(undefined);
            return undefined;
        }
    };

    const refreshToken = async () => {
        if (e2eBypassAuth) {
            setToken(E2E_TOKEN);
            return E2E_TOKEN;
        }
        const user = auth.currentUser;
        if (!user) {
            console.warn("[auth-trace] refreshToken: no current user");
            setToken(undefined);
            return undefined;
        }
        try {
            const idToken = await user.getIdToken(true);
            console.log("[auth-trace] refreshToken resolved token", {
                email: user.email || null,
                tokenLength: tokenLength(idToken)
            });
            setToken(idToken);
            return idToken;
        } catch (error) {
            console.error("Error refreshing auth token:", error);
            setToken(undefined);
            return undefined;
        }
    };

    return (
        <AuthContext.Provider
            value={{
                getAuthUser: () => authUser || (e2eBypassAuth ? E2E_AUTH_USER : null),
                getToken: () => token,
                getTokenAsync: getTokenAsync,
                signIn,
                signInWithEmail,
                signUpWithEmail,
                sendPasswordReset,
                signOut,
                refreshToken
            }}
        >
            {isAuthReady ? (authUser || e2eBypassAuth ? props.children : <AuthButton/>) : null}
        </AuthContext.Provider>
    );
}


export default FirebaseAuth;
